/**
 * bulkReportPdf - 全社分析結果ページの「全員のPDFをダウンロード」用ユーティリティ
 *
 * PDF専用レイアウト (Gb74PersonalReportPdf / A4横2ページ) をオフスクリーンで1人ずつ
 * 描画し、modern-screenshot で画像化して jsPDF に積む。
 *
 * 【A4横2ページ / 1人】
 * 画面表示の縦1カラム4セクションをそのまま1セクション=1ページで積むと、
 * ページ数が多く旧個人レポートの割り付けとも合わないため、PDF は専用レイアウトに
 * 組み替えている (どこに何を置くかは Gb74PersonalReportPdf.tsx のヘッダーコメント参照)。
 * 1ページの実寸は PAGE_W x PAGE_H px 固定 (A4横と同比) で、アスペクト比を保ったまま
 * ページに内接・中央配置する (組織診断PDF = aggregation/utils/pdfGenerator.ts と同方式)。
 *
 * jsPDF の text() は日本語フォントを埋め込んでいないため使えず、氏名等も DOM で画像に含める
 * (氏名/会社/部署/回答日のヘッダーは両ページに入るので、誰のページか常に分かる)。
 *
 * html2canvas は .gb74r のレイアウトを崩すため使用しない (modern-screenshot を採用)。
 * 各ユーザーの表示内容は IndividualReportPage と同一にするため getLatestResult で再取得し、
 * judgment_patterns も findPatternByScores で事前取得して pattern として注入する
 * (コンポーネント内部の非同期取得を避け、描画を確定させてから撮影するため)。
 */
import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { domToCanvas } from 'modern-screenshot';
import { jsPDF } from 'jspdf';
import {
  Gb74PersonalReportPdf,
  PAGE_W,
  PDF_PAGE_SELECTOR,
} from '../components/Gb74PersonalReportPdf';
import { unwrapGb74Scores, getAxisScores } from './gb74Report';
import { analysisApi, type JudgmentPattern } from '../api/analysisApi';

/** 一括ダウンロード対象ユーザー (最低限の識別情報) */
export interface BulkReportUser {
  userId: string;
  displayName: string;
}

/** 進捗コールバック (0-indexed の current / total / 対象名) */
export type BulkProgress = (done: number, total: number, currentName: string) => void;

const RENDER_WIDTH = PAGE_W; // PDFページの実寸 (Gb74PersonalReportPdf が唯一源)
const CAPTURE_SCALE = 3; // 解像度 (組織診断PDFと同じ。8pt前後の文字を鮮明に保つ)
/** JPEG 画質。人数×4ページ積むため、鮮明さを保てる範囲でサイズを抑える */
const JPEG_QUALITY = 0.92;
/**
 * ページ余白・キャンバスの下地色。
 * ページ本体(.gb74rp-page)は元テンプレートと同じパステルのグラデーションを
 * 白の上に重ねて描くため、下地は白にする（従来の #FAFAF8 だと余白だけ
 * 黄みがかって見え、テンプレートと色味が違った）。
 */
const REPORT_BG = '#FFFFFF';
const REPORT_BG_RGB: [number, number, number] = [255, 255, 255]; // REPORT_BG と同値
const A4_W_MM = 297; // A4横 幅
const A4_H_MM = 210; // A4横 高さ
/** ページ余白(mm)。大きくすると縦長セクション(01)の文字が小さくなるため控えめにする */
const PAGE_MARGIN_MM = 6;

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date
    .toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' })
    .replace(/\//g, '.');
};

const IMG_LOAD_TIMEOUT_MS = 5000; // 1枚あたりの画像ロード待ち上限 (発火しない画像でのハング防止)

/** 全 img の読み込み + フォント確定 + 1フレーム待って描画を確定させる */
async function waitForRenderReady(node: HTMLElement): Promise<void> {
  const images = Array.from(node.querySelectorAll('img'));
  await Promise.all(
    images.map((img) =>
      img.complete
        ? Promise.resolve()
        : new Promise<void>((resolve) => {
            // load/error のどちらも発火しない画像 (lazy・ブロック等) で
            // 永久待ちにならないよう、タイムアウトで打ち切って続行する
            let settled = false;
            const done = () => {
              if (settled) return;
              settled = true;
              window.clearTimeout(timer);
              img.removeEventListener('load', done);
              img.removeEventListener('error', done);
              resolve();
            };
            const timer = window.setTimeout(done, IMG_LOAD_TIMEOUT_MS);
            img.addEventListener('load', done);
            img.addEventListener('error', done);
          })
    )
  );
  if (document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch {
      // フォント待ちに失敗しても続行
    }
  }
  // レイアウト確定のため2フレーム + 少し待つ
  await new Promise<void>((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
  );
  await new Promise((resolve) => setTimeout(resolve, 150));
}

/**
 * canvas 1枚を A4横1ページとして pdf に追加する。
 * アスペクト比を保ったまま余白内に内接させ、中央配置する (組織診断PDFと同じ)。
 */
function addCanvasAsLandscapePage(
  pdf: jsPDF,
  canvas: HTMLCanvasElement,
  isFirstPage: boolean
): void {
  if (!isFirstPage) {
    pdf.addPage();
  }

  // 余白部分がレポート背景と地続きに見えるようページ全面を塗る
  pdf.setFillColor(...REPORT_BG_RGB);
  pdf.rect(0, 0, A4_W_MM, A4_H_MM, 'F');

  const boxW = A4_W_MM - PAGE_MARGIN_MM * 2;
  const boxH = A4_H_MM - PAGE_MARGIN_MM * 2;
  const imgRatio = canvas.width / canvas.height;

  let drawW: number;
  let drawH: number;
  if (imgRatio >= boxW / boxH) {
    // A4横と同比かそれより横長なら幅いっぱい
    drawW = boxW;
    drawH = boxW / imgRatio;
  } else {
    // 文言が長くページが縦に伸びた場合は高さ基準で内接させる
    drawH = boxH;
    drawW = boxH * imgRatio;
  }

  const x = (A4_W_MM - drawW) / 2;
  const y = (A4_H_MM - drawH) / 2;

  // jsPDF は JPEG を DCTDecode でそのまま埋め込むため PNG より軽い
  pdf.addImage(canvas.toDataURL('image/jpeg', JPEG_QUALITY), 'JPEG', x, y, drawW, drawH);
}

/**
 * 1人分の Gb74PersonalReportPdf をオフスクリーンで描画し、
 * ページ単位 (=A4横1ページ分) の canvas 配列にする。
 */
async function renderUserReport(
  host: HTMLElement,
  userId: string
): Promise<HTMLCanvasElement[] | null> {
  // IndividualReportPage と同一データを使うため最新結果を再取得
  const result = await analysisApi.getLatestResult(userId);
  const gb74 = unwrapGb74Scores(result.scores?.scores);
  if (!gb74) {
    // gb74形式でない (旧データ) はスキップ
    return null;
  }

  // judgment_patterns を事前取得して注入 (描画を確定させる)
  let pattern: JudgmentPattern | null = null;
  try {
    const a = getAxisScores(gb74);
    pattern = await analysisApi.findPatternByScores({ A: a.A, B: a.B, C: a.C, D: a.D });
  } catch {
    pattern = null;
  }

  const container = document.createElement('div');
  container.style.width = `${RENDER_WIDTH}px`;
  container.style.background = REPORT_BG;
  host.appendChild(container);

  const root: Root = createRoot(container);
  try {
    await new Promise<void>((resolve) => {
      root.render(
        <Gb74PersonalReportPdf
          scores={result.scores.scores}
          userName={result.userName || userId}
          answerDate={formatDate(result.createdAt)}
          companyName={result.companyName || undefined}
          departmentName={result.departmentName || undefined}
          pattern={pattern}
        />
      );
      // render 後のマウントを待つ
      requestAnimationFrame(() => resolve());
    });

    await waitForRenderReady(container);

    const pages = Array.from(container.querySelectorAll<HTMLElement>(PDF_PAGE_SELECTOR));
    if (pages.length === 0) {
      // Gb74PersonalReportPdf が null を返した (gb74形式でない) 等
      return null;
    }

    const canvases: HTMLCanvasElement[] = [];
    for (const page of pages) {
      canvases.push(
        await domToCanvas(page, {
          scale: CAPTURE_SCALE,
          backgroundColor: REPORT_BG,
          width: page.offsetWidth,
          height: page.offsetHeight,
        })
      );
    }

    return canvases;
  } finally {
    root.unmount();
    host.removeChild(container);
  }
}

/**
 * 全員分の個人レポートを1つのA4横PDFにまとめてダウンロードする。
 * @returns 成功件数 / スキップした userId 一覧
 */
export async function downloadAllReportsPdf(
  users: BulkReportUser[],
  onProgress?: BulkProgress
): Promise<{ succeeded: number; skipped: string[] }> {
  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '-99999px';
  host.style.top = '0';
  host.style.zIndex = '-1';
  host.style.pointerEvents = 'none';
  document.body.appendChild(host);

  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const skipped: string[] = [];
  let succeeded = 0;
  let pageAdded = false;

  try {
    for (let i = 0; i < users.length; i++) {
      const user = users[i];
      onProgress?.(i, users.length, user.displayName);
      try {
        const canvases = await renderUserReport(host, user.userId);
        if (!canvases || canvases.length === 0) {
          skipped.push(user.userId);
          continue;
        }
        for (const canvas of canvases) {
          addCanvasAsLandscapePage(pdf, canvas, !pageAdded);
          pageAdded = true;
        }
        succeeded += 1;
      } catch (err) {
        console.error(`レポート生成に失敗しました (userId=${user.userId})`, err);
        skipped.push(user.userId);
      }
    }

    onProgress?.(users.length, users.length, '');

    if (succeeded === 0) {
      throw new Error('PDF化できる分析結果がありませんでした。');
    }

    const stamp = formatDate(new Date().toISOString()).replace(/\./g, '');
    pdf.save(`全社分析レポート_${stamp}.pdf`);

    return { succeeded, skipped };
  } finally {
    document.body.removeChild(host);
  }
}

/**
 * 1名分の個人レポートを A4横2ページの PDF として保存する。
 *
 * 一括版 (downloadAllReportsPdf) と描画経路・寸法・画質をすべて共有しているため、
 * 「画面で見たもの」「単票PDF」「全員PDF」の3つが必ず同じ見た目になる。
 *
 * @param userId  対象社員
 * @param displayName ファイル名に使う表示名（未指定なら userId）
 */
export async function downloadSingleReportPdf(userId: string, displayName?: string): Promise<void> {
  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '-99999px';
  host.style.top = '0';
  host.style.zIndex = '-1';
  host.style.pointerEvents = 'none';
  document.body.appendChild(host);

  try {
    const canvases = await renderUserReport(host, userId);
    if (!canvases || canvases.length === 0) {
      throw new Error('この社員の分析結果はPDF化できません（旧形式の可能性があります）。');
    }

    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    canvases.forEach((canvas, i) => addCanvasAsLandscapePage(pdf, canvas, i === 0));

    const stamp = formatDate(new Date().toISOString()).replace(/\./g, '');
    const safeName = (displayName || userId).replace(/[\\/:*?"<>|]/g, '_');
    pdf.save(`個人レポート_${safeName}_${stamp}.pdf`);
  } finally {
    document.body.removeChild(host);
  }
}
